import SEO from "../../components/SEO";
import { BsSearch } from "react-icons/bs";
import { HiExclamationTriangle, HiInformationCircle, HiMapPin, HiClock, HiShieldCheck } from "react-icons/hi2";
import banner from "../../assets/img/asset7.png";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ParcelOrderService from "../../services/parcel-order.service";
import Input from "../../components/Input";
import OrderCard from "../../components/OrderCard";

export const TrackParcelPage = () => {
	const [searchParams] = useSearchParams();
	const [orderState, setOrderState] = useState({
		order: {},
		reference_number: searchParams.get("ref") || "",
		show_results: false,
		hasError: false
	});

	const { order, reference_number, show_results, hasError } = orderState;

	const handleChange = (e) => {
		setOrderState({ ...orderState, [e.target.id]: e.target.value });
	};

	const lookupOrder = (ref) => {
		if (!ref) return;
		ParcelOrderService.findOrder(ref).then((data) => {
			if (data && data.id) {
				setOrderState((prevState) => ({
					...prevState,
					order: data,
					reference_number: ref,
					show_results: true,
					hasError: false
				}));
			} else {
				setOrderState((prevState) => ({
					...prevState,
					order: null,
					show_results: false,
					hasError: true
				}));
			}
		}).catch(() => {
			setOrderState((prevState) => ({
				...prevState,
				order: null,
				show_results: false,
				hasError: true
			}));
		});
	};

	// Auto-load from URL param
	useEffect(() => {
		const ref = searchParams.get("ref");
		if (ref) lookupOrder(ref);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSubmit = (event) => {
		event.preventDefault();
		if (reference_number === "") {
			setOrderState((prevState) => ({
				...prevState,
				show_results: false,
				hasError: true
			}));
			return;
		}
		lookupOrder(reference_number);
	};

	return (
		<div className="w-full min-h-screen flex flex-col bg-gradient-to-br from-cyan-100 to-fuchsia-200"><SEO title="Track Your Parcel" description="Track your Parcelin delivery in real-time. Enter your tracking number to see live updates on your parcel status." path="/track_parcel" />
			{/* Hero Section */}
			<div className="max-w-7xl mx-auto px-5 py-12 lg:py-20 w-full">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Search Form */}
					<div className="flex flex-col gap-6">
						<div className="text-center lg:text-left">
							<h1 className="text-4xl md:text-5xl font-bold text-purple-800 mb-4">
								Track Your Parcel
							</h1>
							<p className="text-lg text-purple-700">
								Enter your tracking number to see real-time updates on your delivery
							</p>
						</div>

						{/* Search Card */}
						<div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8">
							<form className="space-y-4" onSubmit={handleSubmit}>
								<div>
									<label htmlFor="reference_number" className="block text-sm font-semibold text-gray-700 mb-2">
										Tracking Number
									</label>
									<Input
										handleChange={handleChange}
										value={reference_number}
										labelText=""
										labelFor="reference_number"
										id="reference_number"
										name="reference_number"
										type="text"
										placeholder="Enter tracking number (e.g., PCL123456)"
										customClass="w-full bg-white border-2 border-gray-300 hover:border-purple-400 focus:border-purple-500 px-4 py-3 rounded-xl shadow-sm leading-tight focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition-all text-base"
									/>
								</div>
								<button
									type="submit"
									className="w-full flex gap-3 items-center justify-center py-4 px-6 border border-transparent font-bold rounded-xl text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all shadow-lg hover:shadow-xl text-base"
								>
									<BsSearch size={20} />
									Track Parcel
								</button>
							</form>
						</div>

						{/* Results Section */}
						{show_results && order && order.id && (
							<div className="animate-slideDown">
								<OrderCard order={order} />
							</div>
						)}

						{/* Error Message */}
						{hasError && (
							<div className="bg-red-50 border-2 border-red-400 rounded-xl p-6 animate-slideDown">
								<div className="flex items-start gap-3">
									<div className="flex-shrink-0">
										<HiExclamationTriangle className="h-6 w-6 text-red-600" />
									</div>
									<div>
										<h3 className="text-red-800 font-bold text-lg mb-1">
											Parcel Not Found
										</h3>
										<p className="text-red-700 text-sm">
											No parcel found with tracking number <span className="font-semibold">"{reference_number}"</span>. Please check the number and try again.
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Initial Help Message */}
						{!show_results && !hasError && (
							<div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
								<div className="flex items-start gap-3">
									<div className="flex-shrink-0">
										<HiInformationCircle className="h-6 w-6 text-purple-600" />
									</div>
									<div>
										<h3 className="text-purple-800 font-bold text-base mb-1">
											How to Track
										</h3>
										<p className="text-purple-700 text-sm">
											Enter your tracking number above to see your parcel's current status, location, and estimated delivery time.
										</p>
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Right Column - Illustration */}
					<div className="flex flex-col items-center justify-center lg:order-last order-first">
						<div className="text-center mb-8 lg:hidden">
							<h2 className="text-3xl font-bold text-purple-800">
								Track Your Parcel, Right.
							</h2>
						</div>
						<div className="relative">
							<div className="absolute inset-0 bg-purple-200 rounded-full blur-3xl opacity-30"></div>
							<img
								className="relative h-64 md:h-96 lg:h-[28rem] object-contain drop-shadow-2xl"
								src={banner}
								alt="Parcelin Tracking"
							/>
						</div>
						<div className="hidden lg:block text-center mt-8">
							<h2 className="text-3xl md:text-4xl font-bold text-purple-800">
								Track Your Parcel, Right.
							</h2>
						</div>
					</div>
				</div>
			</div>

			{/* Features Section */}
			<div className="bg-white py-12">
				<div className="max-w-6xl mx-auto px-5">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						<div className="text-center">
							<div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<HiMapPin className="w-8 h-8 text-purple-600" />
							</div>
							<h3 className="text-xl font-semibold text-gray-800 mb-2">Real-Time Location</h3>
							<p className="text-gray-600">Track your parcel's exact location in real-time</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<HiClock className="w-8 h-8 text-purple-600" />
							</div>
							<h3 className="text-xl font-semibold text-gray-800 mb-2">Delivery Updates</h3>
							<p className="text-gray-600">Get instant notifications on delivery status</p>
						</div>
						<div className="text-center">
							<div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<HiShieldCheck className="w-8 h-8 text-purple-600" />
							</div>
							<h3 className="text-xl font-semibold text-gray-800 mb-2">Secure Tracking</h3>
							<p className="text-gray-600">Your tracking information is safe and private</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
